// src/pages/admin/AdminAssets.jsx
// ============================================================
// Asset Management — Admin & HR shared module
// Full CRUD: Add / Edit / Assign / Return / Maintenance / Retire
// Real-time Firestore backend | Audit trail via assetHistory
// ============================================================

import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useAllUsers } from "../../hooks/useFirestore";
import {
  useAssets,
  useAssetHistory,
  createAsset,
  updateAsset,
  assignAsset,
  reassignAsset,
  returnAsset,
  setAssetMaintenance,
  retireAsset,
  deleteAsset,
} from "../../hooks/useAssets";
import { ADMIN_NAV, HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import {
  Laptop,
  Box,
  CheckCircle2,
  UserCheck,
  Wrench,
  Trash2,
  Archive,
  History,
  Edit3,
  UserPlus,
  RefreshCw,
  CornerUpLeft,
  Plus,
  Search,
} from "lucide-react";

// ── Constants ─────────────────────────────────────────────
const CATEGORIES = [
  "Laptop",
  "Monitor",
  "Mobile",
  "Keyboard",
  "Mouse",
  "Headset",
  "Docking Station",
  "Software License",
  "Printer",
  "Other",
];

const STATUSES = [
  { value: "available", label: "Available", color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  { value: "assigned", label: "Assigned", color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  {
    value: "under_maintenance",
    label: "Under Maintenance",
    color: "#166534",
    bg: "#f0fdf4",
    border: "#86efac",
  },
  { value: "retired", label: "Retired", color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
];

const ACTION_COLORS = {
  created: { color: "#15803d", bg: "#f0fdf4", label: "Created" },
  assigned: { color: "#15803d", bg: "#f0fdf4", label: "Assigned" },
  returned: { color: "#15803d", bg: "#f0fdf4", label: "Returned" },
  under_maintenance: {
    color: "#166534",
    bg: "#f0fdf4",
    label: "Maintenance",
  },
  retired: { color: "#64748b", bg: "#f8fafc", label: "Retired" },
  updated: { color: "#15803d", bg: "#f0fdf4", label: "Updated" },
};

const PAGE_SIZE = 10;

function statusInfo(status) {
  return (
    STATUSES.find((s) => s.value === status) || {
      value: status,
      label: status,
      color: "#6b7280",
      bg: "#f3f4f6",
    }
  );
}

function fmtDate(ts) {
  if (!ts) return "—";
  if (typeof ts === "string") {
    const d = new Date(ts + "T00:00:00");
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtDateTime(ts) {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getInitials(name = "") {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const AVATAR_COLORS = [
  "#4f46e5","#0ea5e9","#10b981","#f59e0b",
  "#ef4444","#8b5cf6","#ec4899","#1b4b31",
];
function avatarColor(name = "") {
  let h = 0;
  for (let c of name) h = c.charCodeAt(0) + h * 31;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ── Asset History Drawer ──────────────────────────────────
function AssetHistoryDrawer({ asset, onClose }) {
  const { data: history, loading } = useAssetHistory(asset?.id);

  if (!asset) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 900,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "flex-end",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(2px)",
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: "relative",
          width: "min(480px, 95vw)",
          background: "var(--bg-card, #fff)",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid var(--border, #e5e7eb)",
            background: "var(--bg-secondary, #f9fafb)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--text-primary)",
              }}
            >
              {asset.name}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                marginTop: 2,
              }}
            >
              {asset.assetTag} · {asset.category}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.4rem",
              cursor: "pointer",
              color: "var(--text-muted)",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Asset meta */}
        <div
          style={{
            padding: "1rem 1.5rem",
            borderBottom: "1px solid var(--border, #e5e7eb)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.6rem",
          }}
        >
          {[
            ["Brand / Model", `${asset.brand || "—"} ${asset.model || ""}`],
            ["Serial No.", asset.serialNumber || "—"],
            ["Purchase Date", fmtDate(asset.purchaseDate)],
            ["Warranty Expiry", fmtDate(asset.warrantyExpiry)],
            [
              "Status",
              <span
                key="s"
                style={{
                  padding: "2px 8px",
                  borderRadius: 99,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  background: statusInfo(asset.status).bg,
                  color: statusInfo(asset.status).color,
                }}
              >
                {statusInfo(asset.status).label}
              </span>,
            ],
            [
              "Assigned To",
              asset.assignedToName
                ? `${asset.assignedToName} (${asset.assignedToEmpId || "—"})`
                : "—",
            ],
          ].map(([key, val]) => (
            <div key={key}>
              <div
                style={{
                  fontSize: "0.68rem",
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  marginBottom: 2,
                }}
              >
                {key}
              </div>
              <div
                style={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                {val}
              </div>
            </div>
          ))}
        </div>

        {/* History Timeline */}
        <div style={{ padding: "1rem 1.5rem", flex: 1 }}>
          <div
            style={{
              fontSize: "0.78rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--text-muted)",
              marginBottom: "0.85rem",
            }}
          >
            Asset History
          </div>
          {loading ? (
            <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem 0" }}>
              Loading history…
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem 0" }}>
              No history yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {history.map((h) => {
                const ac = ACTION_COLORS[h.action] || ACTION_COLORS.updated;
                return (
                  <div
                    key={h.id}
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        flexShrink: 0,
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: ac.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: ac.color,
                      }}
                    >
                      {getInitials(h.performedByName)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            color: "var(--text-primary)",
                          }}
                        >
                          {h.performedByName}
                        </span>
                        <span
                          style={{
                            padding: "1px 7px",
                            borderRadius: 99,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            background: ac.bg,
                            color: ac.color,
                          }}
                        >
                          {ac.label}
                        </span>
                      </div>
                      {h.notes && (
                        <div
                          style={{
                            fontSize: "0.74rem",
                            color: "var(--text-secondary)",
                            marginTop: 2,
                          }}
                        >
                          {h.notes}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "0.68rem",
                          color: "var(--text-muted)",
                          marginTop: 2,
                        }}
                      >
                        {fmtDateTime(h.timestamp)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Assign / Reassign Asset Modal ───────────────────────────
function AssignModal({ asset, users, onClose, onAssign, saving }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const isReassign = asset?.status === "assigned";

  const eligible = useMemo(() => {
    const s = search.toLowerCase();
    return users
      .filter(
        (u) =>
          u.role === "employee" || u.role === "lead" || u.role === "hr"
      )
      .filter(
        (u) =>
          (u.displayName || "").toLowerCase().includes(s) ||
          (u.employeeId || "").toLowerCase().includes(s) ||
          (u.email || "").toLowerCase().includes(s)
      )
      .slice(0, 30);
  }, [users, search]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "var(--bg-card, #fff)",
          borderRadius: 16,
          width: "min(500px, 95vw)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid var(--border, #e5e7eb)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: isReassign ? "#fffbeb" : "var(--bg-card, #fff)",
          }}
        >
          <div>
            <div style={{ fontSize: "1rem", fontWeight: 700 }}>
              {isReassign ? "🔄 Reassign Asset" : "👤 Assign Asset"}
            </div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 2 }}>
              {asset?.assetTag} — {asset?.name}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.4rem",
              cursor: "pointer",
              color: "var(--text-muted)",
            }}
          >
            ×
          </button>
        </div>

        {/* Currently assigned banner */}
        {isReassign && asset?.assignedToName && (
          <div
            style={{
              margin: "1rem 1.5rem 0",
              padding: "0.6rem 0.85rem",
              background: "#dbeafe",
              borderRadius: 8,
              fontSize: "0.8rem",
              color: "#1e40af",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <span style={{ fontWeight: 700 }}>Currently assigned to:</span>
            <span>
              {asset.assignedToName}
              {asset.assignedToEmpId ? ` (${asset.assignedToEmpId})` : ""}
            </span>
          </div>
        )}

        <div style={{ padding: "1rem 1.5rem 1.25rem" }}>
          <input
            type="text"
            placeholder="🔍 Search by name, Employee ID or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "0.6rem 0.9rem",
              border: "1px solid var(--border, #d1d5db)",
              borderRadius: 8,
              fontSize: "0.84rem",
              outline: "none",
              background: "var(--bg-secondary, #f9fafb)",
              boxSizing: "border-box",
            }}
            autoFocus
          />

          <div
            style={{
              marginTop: "0.75rem",
              maxHeight: 280,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "0.4rem",
            }}
          >
            {eligible.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "var(--text-muted)",
                  padding: "1.5rem 0",
                  fontSize: "0.84rem",
                }}
              >
                No employees found.
              </div>
            )}
            {eligible.map((u) => {
              const isCurrentAssignee =
                isReassign &&
                (u.uid === asset?.assignedTo || u.id === asset?.assignedTo);
              return (
                <div
                  key={u.id}
                  onClick={() =>
                    !isCurrentAssignee &&
                    setSelected(selected?.id === u.id ? null : u)
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.6rem 0.75rem",
                    borderRadius: 10,
                    cursor: isCurrentAssignee ? "not-allowed" : "pointer",
                    opacity: isCurrentAssignee ? 0.5 : 1,
                    border: `2px solid ${
                      selected?.id === u.id
                        ? "var(--primary, #1b4b31)"
                        : isCurrentAssignee
                        ? "#fbbf24"
                        : "transparent"
                    }`,
                    background:
                      selected?.id === u.id
                        ? "rgba(27,75,49,0.06)"
                        : isCurrentAssignee
                        ? "#fefce8"
                        : "var(--bg-secondary, #f9fafb)",
                    transition: "all 0.15s",
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: avatarColor(u.displayName || ""),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(u.displayName || "")}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                      }}
                    >
                      {u.displayName}
                      {isCurrentAssignee && (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            padding: "1px 6px",
                            borderRadius: 99,
                            background: "#fef3c7",
                            color: "#92400e",
                            fontWeight: 700,
                          }}
                        >
                          Current
                        </span>
                      )}
                    </div>
                    <div
                      style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}
                    >
                      {u.employeeId || "—"} · {u.department || "—"} ·{" "}
                      {u.designation || u.role || "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              marginTop: "1rem",
              justifyContent: "flex-end",
            }}
          >
            <button className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={!selected || saving}
              onClick={() => onAssign(selected)}
              style={{
                background: isReassign ? "#d97706" : undefined,
                borderColor: isReassign ? "#d97706" : undefined,
              }}
            >
              {saving
                ? isReassign
                  ? "Reassigning…"
                  : "Assigning…"
                : isReassign
                ? `Reassign to ${selected?.displayName || "…"}`
                : `Assign to ${selected?.displayName || "…"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Add / Edit Asset Modal ──────────────────────────────────
const EMPTY_FORM = {
  name: "",
  category: "Laptop",
  brand: "",
  model: "",
  serialNumber: "",
  purchaseDate: "",
  warrantyExpiry: "",
  notes: "",
};

function AssetFormModal({ editItem, onClose, onSave, saving, error }) {
  const [form, setForm] = useState(
    editItem
      ? {
          name: editItem.name || "",
          category: editItem.category || "Laptop",
          brand: editItem.brand || "",
          model: editItem.model || "",
          serialNumber: editItem.serialNumber || "",
          purchaseDate: editItem.purchaseDate || "",
          warrantyExpiry: editItem.warrantyExpiry || "",
          notes: editItem.notes || "",
        }
      : EMPTY_FORM
  );

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "var(--bg-card, #fff)",
          borderRadius: 16,
          width: "min(560px, 95vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid var(--border, #e5e7eb)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            background: "var(--bg-card, #fff)",
            zIndex: 10,
          }}
        >
          <div style={{ fontSize: "1rem", fontWeight: 700 }}>
            {editItem ? "Edit Asset" : "Add New Asset"}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.4rem",
              cursor: "pointer",
              color: "var(--text-muted)",
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: "1.25rem 1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {error && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                padding: "0.65rem 1rem",
                fontSize: "0.82rem",
                color: "#b91c1c",
              }}
            >
              {error}
            </div>
          )}

          {/* Row 1: Name + Category */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <label className="form-label">Asset Name *</label>
              <input
                className="form-input"
                placeholder="e.g. Dell Laptop XPS 15"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Category *</label>
              <select
                className="form-input"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Brand + Model */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <label className="form-label">Brand</label>
              <input
                className="form-input"
                placeholder="e.g. Dell"
                value={form.brand}
                onChange={(e) => set("brand", e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Model</label>
              <input
                className="form-input"
                placeholder="e.g. XPS 15 9510"
                value={form.model}
                onChange={(e) => set("model", e.target.value)}
              />
            </div>
          </div>

          {/* Serial Number */}
          <div>
            <label className="form-label">Serial Number</label>
            <input
              className="form-input"
              placeholder="Unique serial number"
              value={form.serialNumber}
              onChange={(e) => set("serialNumber", e.target.value)}
            />
          </div>

          {/* Row 3: Purchase Date + Warranty */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <label className="form-label">Purchase Date</label>
              <input
                type="date"
                className="form-input"
                value={form.purchaseDate}
                onChange={(e) => set("purchaseDate", e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Warranty Expiry</label>
              <input
                type="date"
                className="form-input"
                value={form.warrantyExpiry}
                onChange={(e) => set("warrantyExpiry", e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              rows={2}
              placeholder="Optional notes…"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              style={{ resize: "vertical" }}
            />
          </div>

          {/* Actions */}
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
              paddingTop: "0.5rem",
            }}
          >
            <button className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={saving}
              onClick={() => onSave(form)}
            >
              {saving
                ? editItem
                  ? "Saving…"
                  : "Adding…"
                : editItem
                ? "Save Changes"
                : "Add Asset"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────
export default function AdminAssets() {
  const { userProfile } = useAuth();
  const { data: assets, loading } = useAssets();
  const { data: allUsers = [] } = useAllUsers();

  const isAdmin = userProfile?.role === "admin";
  const isHR = userProfile?.role === "hr";
  const canManage = isAdmin || isHR;

  // ── UI State ────────────────────────────────────────────
  const [searchQ, setSearchQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [historyAsset, setHistoryAsset] = useState(null);
  const [assignAsset_, setAssignAsset_] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const [actionLoading, setActionLoading] = useState({});

  // ── Filter & Search ─────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQ.toLowerCase();
    return assets.filter((a) => {
      const matchSearch =
        !q ||
        (a.name || "").toLowerCase().includes(q) ||
        (a.assetTag || "").toLowerCase().includes(q) ||
        (a.brand || "").toLowerCase().includes(q) ||
        (a.model || "").toLowerCase().includes(q) ||
        (a.serialNumber || "").toLowerCase().includes(q) ||
        (a.assignedToName || "").toLowerCase().includes(q);
      const matchCat =
        filterCat === "all" || a.category === filterCat;
      const matchStatus =
        filterStatus === "all" || a.status === filterStatus;
      return matchSearch && matchCat && matchStatus;
    });
  }, [assets, searchQ, filterCat, filterStatus]);

  // ── KPIs ────────────────────────────────────────────────
  const kpi = useMemo(() => {
    return {
      total: assets.length,
      available: assets.filter((a) => a.status === "available").length,
      assigned: assets.filter((a) => a.status === "assigned").length,
      maintenance: assets.filter((a) => a.status === "under_maintenance").length,
      retired: assets.filter((a) => a.status === "retired").length,
    };
  }, [assets]);

  // ── Pagination ──────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [searchQ, filterCat, filterStatus]);

  // ── Add/Edit Submit ─────────────────────────────────────
  async function handleFormSave(form) {
    if (!form.name.trim() || !form.category) {
      setFormError("Asset name and category are required.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      if (editItem) {
        await updateAsset(editItem.id, form, userProfile);
      } else {
        await createAsset(form, userProfile);
      }
      setShowForm(false);
      setEditItem(null);
    } catch (err) {
      setFormError(err.message || "Failed to save asset.");
    }
    setSaving(false);
  }

  // ── Assign / Reassign ────────────────────────────────────
  async function handleAssign(employee) {
    if (!assignAsset_) return;
    setAssigning(true);
    try {
      if (assignAsset_.status === "assigned") {
        // Atomic single-write reassign
        await reassignAsset(
          assignAsset_.id,
          employee,
          userProfile,
          assignAsset_.assignedToName
        );
      } else {
        await assignAsset(assignAsset_.id, employee, userProfile);
      }
      setAssignAsset_(null);
    } catch (err) {
      alert(err.message || "Failed to assign asset.");
    }
    setAssigning(false);
  }

  // ── Quick Actions ────────────────────────────────────────
  async function handleAction(assetId, action, notes = "") {
    setActionLoading((p) => ({ ...p, [assetId]: action }));
    try {
      if (action === "return") await returnAsset(assetId, userProfile, notes);
      if (action === "maintenance") await setAssetMaintenance(assetId, userProfile, notes);
      if (action === "retire") {
        if (!window.confirm("Are you sure you want to retire this asset? This cannot be undone easily.")) return;
        await retireAsset(assetId, userProfile, notes);
      }
      if (action === "delete") {
        if (!window.confirm("Are you sure you want to permanently delete this asset? This cannot be undone.")) return;
        await deleteAsset(assetId, userProfile);
      }
    } catch (err) {
      alert(err.message || "Action failed.");
    }
    setActionLoading((p) => ({ ...p, [assetId]: null }));
  }

  // ── Render ───────────────────────────────────────────────
  let navItems = ADMIN_NAV;
  let panelLabel = "Admin Panel";
  if (userProfile?.role === "hr") {
    navItems = HR_NAV;
    panelLabel = "HR Panel";
  }

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <style>{`
        .asset-header-banner {
          background: #1b4b31;
          border-radius: 16px;
          padding: 1.35rem 1.75rem;
          color: #ffffff;
          box-shadow: 0 4px 20px rgba(27, 75, 49, 0.15);
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .asset-kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 1rem;
          margin-bottom: 1.25rem;
        }
        .asset-kpi {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 1rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.85rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.03);
          transition: all 0.2s ease;
        }
        .asset-kpi:hover {
          border-color: #10b981;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.08);
        }
        .asset-kpi__icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #f0fdf4;
          border: 1.5px solid #bbf7d0;
          color: #15803d;
        }
        .asset-kpi__val {
          font-size: 1.55rem;
          font-weight: 800;
          line-height: 1;
          color: #0f172a;
        }
        .asset-kpi__label {
          font-size: 0.72rem;
          color: #64748b;
          margin-top: 4px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-weight: 700;
        }
        .asset-filters {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-bottom: 1.25rem;
          align-items: center;
          background: #ffffff;
          padding: 0.85rem 1.1rem;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
        }
        .asset-search-box {
          flex: 1;
          min-width: 240px;
          position: relative;
        }
        .asset-search-input {
          width: 100%;
          padding: 0.5rem 0.85rem 0.5rem 2.3rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.84rem;
          background: #ffffff;
          color: #0f172a;
          outline: none;
        }
        .asset-search-input:focus {
          border-color: #15803d;
        }
        .asset-table-wrap {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.03);
        }
        .asset-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.83rem;
        }
        .asset-table th {
          background: #f8fafc;
          text-align: left;
          padding: 0.7rem 0.9rem;
          font-size: 0.74rem;
          font-weight: 700;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 1.5px solid #e2e8f0;
          white-space: nowrap;
        }
        .asset-table td {
          padding: 0.75rem 0.9rem;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        .asset-table tbody tr:last-child td {
          border-bottom: none;
        }
        .asset-table tbody tr:hover td {
          background: #f8fafc;
        }
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 3px 9px;
          border-radius: 99px;
          font-size: 0.72rem;
          font-weight: 700;
          white-space: nowrap;
          border: 1.5px solid #bbf7d0;
          background: #f0fdf4;
          color: #15803d;
        }
        .asset-actions {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
        }
        .act-btn-outline {
          padding: 4px 9px;
          border-radius: 7px;
          font-size: 0.74rem;
          font-weight: 700;
          border: 1px solid #cbd5e1;
          background: #ffffff;
          color: #475569;
          cursor: pointer;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          transition: all 0.15s ease;
        }
        .act-btn-outline:hover {
          background: #f8fafc;
          border-color: #94a3b8;
        }
        .act-btn-green {
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
          padding: 4px 9px;
          border-radius: 7px;
          font-size: 0.74rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          transition: all 0.15s ease;
        }
        .act-btn-green:hover {
          background: #dcfce7;
          border-color: #86efac;
        }
        .btn-green-banner {
          background: #ffffff;
          color: #1b4b31;
          border: none;
          border-radius: 10px;
          padding: 0.55rem 1.1rem;
          font-weight: 800;
          font-size: 0.82rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          transition: all 0.18s ease;
        }
        .btn-green-banner:hover {
          background: #f0fdf4;
          transform: translateY(-1px);
        }
        .asset-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.85rem 1.1rem;
          border-top: 1px solid #e2e8f0;
          font-size: 0.8rem;
          color: #64748b;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .asset-pagination .pg-btns { display: flex; gap: 0.35rem; }
        .asset-pagination .pg-btns button {
          padding: 4px 10px;
          border-radius: 7px;
          border: 1px solid #cbd5e1;
          background: #ffffff;
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
          color: #334155;
        }
        .asset-pagination .pg-btns button.active {
          background: #1b4b31;
          color: #ffffff;
          border-color: #1b4b31;
        }
        .asset-pagination .pg-btns button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .empty-state {
          text-align: center;
          padding: 3.5rem 1rem;
          color: #64748b;
        }
        .empty-state-title {
          font-size: 1.05rem;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 0.4rem;
        }
      `}</style>

      {/* Page Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem",
        marginBottom: "1.25rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <Laptop size={24} strokeWidth={1.8} color="#1b4b31" />
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, letterSpacing: "-0.02em", color: "#1b4b31" }}>
            Asset Management
          </h1>
        </div>
        {canManage && (
          <button
            style={{
              background: "#1b4b31",
              color: "#ffffff",
              border: "none",
              borderRadius: "9px",
              padding: "0.5rem 1rem",
              fontWeight: 700,
              fontSize: "0.82rem",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem"
            }}
            onClick={() => {
              setEditItem(null);
              setFormError("");
              setShowForm(true);
            }}
          >
            <Plus size={16} strokeWidth={2.5} /> Add Asset
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="asset-kpi-grid">
        {[
          { Icon: Box, label: "Total Assets", val: kpi.total },
          { Icon: CheckCircle2, label: "Available", val: kpi.available },
          { Icon: UserCheck, label: "Assigned", val: kpi.assigned },
          { Icon: Wrench, label: "Maintenance", val: kpi.maintenance },
          { Icon: Trash2, label: "Retired", val: kpi.retired },
        ].map(({ Icon, label, val }) => (
          <div className="asset-kpi" key={label}>
            <div className="asset-kpi__icon">
              <Icon size={20} strokeWidth={1.8} />
            </div>
            <div>
              <div className="asset-kpi__val">{val}</div>
              <div className="asset-kpi__label">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="asset-filters">
        <div className="asset-search-box">
          <Search size={15} strokeWidth={1.8} style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input
            className="asset-search-input"
            type="text"
            placeholder="Search by name, tag, brand, serial, or assignee…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          style={{ padding: "0.5rem 0.85rem", borderRadius: "10px", border: "1.5px solid #cbd5e1", fontSize: "0.82rem", background: "#ffffff", color: "#0f172a", fontWeight: 500 }}
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          style={{ padding: "0.5rem 0.85rem", borderRadius: "10px", border: "1.5px solid #cbd5e1", fontSize: "0.82rem", background: "#ffffff", color: "#0f172a", fontWeight: 500 }}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Statuses</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="empty-state">
          <div className="hrms-spinner" style={{ margin: "0 auto 1rem" }} />
          <div className="empty-state-title">Loading assets…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <Laptop size={44} strokeWidth={1.5} style={{ margin: "0 auto 0.75rem", opacity: 0.35 }} />
          <div className="empty-state-title">No assets found</div>
          <p style={{ fontSize: "0.84rem" }}>
            {assets.length === 0
              ? "Add your first asset to get started."
              : "Try adjusting the search or filters."}
          </p>
          {canManage && assets.length === 0 && (
            <button
              className="btn-primary"
              style={{ marginTop: "1rem", background: "#1b4b31", color: "#fff" }}
              onClick={() => {
                setEditItem(null);
                setFormError("");
                setShowForm(true);
              }}
            >
              + Add First Asset
            </button>
          )}
        </div>
      ) : (
        <div className="asset-table-wrap">
          <div style={{ overflowX: "auto" }}>
            <table className="asset-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Asset Tag</th>
                  <th>Name / Category</th>
                  <th>Brand / Model</th>
                  <th>Serial No.</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Purchase / Warranty</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paginated.map((asset, idx) => {
                  const si = statusInfo(asset.status);
                  const busy = actionLoading[asset.id];
                  return (
                    <tr key={asset.id}>
                      <td style={{ color: "#64748b", fontSize: "0.78rem" }}>
                        {(page - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.78rem",
                            fontWeight: 800,
                            color: "#15803d",
                            background: "#f0fdf4",
                            border: "1.5px solid #bbf7d0",
                            padding: "2px 8px",
                            borderRadius: 6,
                          }}
                        >
                          {asset.assetTag || "—"}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{asset.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                          {asset.category}
                        </div>
                      </td>
                      <td style={{ fontSize: "0.8rem", color: "#334155" }}>
                        <div>{asset.brand || "—"}</div>
                        {asset.model && (
                          <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                            {asset.model}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            color: "#475569",
                          }}
                        >
                          {asset.serialNumber || "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ background: si.bg, color: si.color, borderColor: si.border || "#bbf7d0" }}
                        >
                          {si.label}
                        </span>
                      </td>
                      <td>
                        {asset.assignedToName ? (
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: "0.83rem",
                                color: "#0f172a",
                              }}
                            >
                              {asset.assignedToName}
                            </div>
                            <div
                              style={{
                                fontSize: "0.72rem",
                                color: "#64748b",
                              }}
                            >
                              {asset.assignedToEmpId || ""}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                            —
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: "0.75rem", color: "#475569" }}>
                        <div>{fmtDate(asset.purchaseDate)}</div>
                        {asset.warrantyExpiry && (
                          <div style={{ color: "#64748b", marginTop: 2 }}>
                            Warranty: {fmtDate(asset.warrantyExpiry)}
                          </div>
                        )}
                      </td>
                      {canManage && (
                        <td>
                          <div className="asset-actions">
                            {/* History */}
                            <button
                              className="act-btn-outline"
                              onClick={() => setHistoryAsset(asset)}
                              title="View History"
                            >
                              <History size={12} strokeWidth={1.8} /> History
                            </button>

                            {/* Edit */}
                            <button
                              className="act-btn-outline"
                              onClick={() => {
                                setEditItem(asset);
                                setFormError("");
                                setShowForm(true);
                              }}
                              title="Edit"
                            >
                              <Edit3 size={12} strokeWidth={1.8} /> Edit
                            </button>

                            {/* Assign (available only) */}
                            {asset.status === "available" && (
                              <button
                                className="act-btn-green"
                                onClick={() => setAssignAsset_(asset)}
                                disabled={!!busy}
                                title="Assign to Employee"
                              >
                                <UserPlus size={12} strokeWidth={2} /> Assign
                              </button>
                            )}

                            {/* Reassign (assigned assets) */}
                            {asset.status === "assigned" && (
                              <button
                                className="act-btn-green"
                                onClick={() => setAssignAsset_(asset)}
                                disabled={!!busy}
                                title="Reassign to another Employee"
                              >
                                <RefreshCw size={12} strokeWidth={2} /> Reassign
                              </button>
                            )}

                            {/* Return */}
                            {asset.status === "assigned" && (
                              <button
                                className="act-btn-outline"
                                onClick={() => handleAction(asset.id, "return")}
                                disabled={!!busy}
                                title="Return Asset"
                              >
                                <CornerUpLeft size={12} strokeWidth={1.8} /> {busy === "return" ? "…" : "Return"}
                              </button>
                            )}

                            {/* Maintenance */}
                            {(asset.status === "available" || asset.status === "assigned") && (
                              <button
                                className="act-btn-outline"
                                onClick={() => handleAction(asset.id, "maintenance")}
                                disabled={!!busy}
                                title="Send to Maintenance"
                              >
                                <Wrench size={12} strokeWidth={1.8} /> {busy === "maintenance" ? "…" : "Maintenance"}
                              </button>
                            )}

                            {/* Back to Available from Maintenance */}
                            {asset.status === "under_maintenance" && (
                              <button
                                className="act-btn-green"
                                onClick={() => handleAction(asset.id, "return", "Returned from maintenance")}
                                disabled={!!busy}
                                title="Mark Available"
                              >
                                <CheckCircle2 size={12} strokeWidth={2} /> {busy === "return" ? "…" : "Available"}
                              </button>
                            )}

                            {/* Retire (Admin only) */}
                            {isAdmin && asset.status !== "retired" && (
                              <button
                                className="act-btn-outline"
                                style={{ color: "#d97706", borderColor: "#fde68a" }}
                                onClick={() => handleAction(asset.id, "retire")}
                                disabled={!!busy}
                                title="Retire Asset"
                              >
                                <Archive size={12} strokeWidth={1.8} /> {busy === "retire" ? "…" : "Retire"}
                              </button>
                            )}

                            {/* Delete (Permanently delete asset) */}
                            {canManage && (
                              <button
                                className="act-btn-outline"
                                style={{ color: "#dc2626", borderColor: "#fca5a5" }}
                                onClick={() => handleAction(asset.id, "delete")}
                                disabled={!!busy}
                                title="Permanently Delete Asset"
                              >
                                <Trash2 size={12} strokeWidth={1.8} /> {busy === "delete" ? "…" : "Delete"}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                      {!canManage && (
                        <td>
                          <button
                            className="act-btn-outline"
                            onClick={() => setHistoryAsset(asset)}
                          >
                            <History size={12} strokeWidth={1.8} /> History
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="asset-pagination">
            <span>
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–
              {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="pg-btns">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹ Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (p) =>
                    p === 1 ||
                    p === totalPages ||
                    Math.abs(p - page) <= 1
                )
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && arr[i - 1] !== p - 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} style={{ padding: "4px 6px", color: "var(--text-muted)" }}>
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={page === p ? "active" : ""}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals & Drawers */}
      {showForm && (
        <AssetFormModal
          editItem={editItem}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
          }}
          onSave={handleFormSave}
          saving={saving}
          error={formError}
        />
      )}

      {assignAsset_ && (
        <AssignModal
          asset={assignAsset_}
          users={allUsers}
          onClose={() => setAssignAsset_(null)}
          onAssign={handleAssign}
          saving={assigning}
        />
      )}

      {historyAsset && (
        <AssetHistoryDrawer
          asset={historyAsset}
          onClose={() => setHistoryAsset(null)}
        />
      )}
    </Layout>
  );
}
